import dayjs from 'dayjs'
import { useLocalStorage } from '@vueuse/core'
import token from '@/constants/token'

import type { TokenService } from '@/types'

class TokenServiceImpl implements TokenService {
  readonly dayjs = dayjs
  readonly event: string[] = ['input', 'click', 'wheel', 'ontouchstart', 'touchmove']
  readonly tokenExpirationHeader: string = token.TOKEN_EXPIRATION_HEADER_NAME

  readonly refreshInterval = {
    interval: null as NodeJS.Timeout | null,
    intervalTimeout: 10000
  }

  readonly tokenGuardInterval = {
    interval: null as NodeJS.Timeout | null,
    intervalTimeout: 1000
  }

  // Using VueUse composables for persistent storage
  private tokenExpirationStorage = useLocalStorage(token.TOKEN_EXPIRATION_KEY, '0')
  private lastActivityStorage = useLocalStorage(token.LAST_ACTIVITY_TIMESTAMP_KEY, '0')

  startRefreshInterval(handler: () => void): void {
    this.refreshInterval.interval = setInterval(() => {
      /*
       * do nothing, there is no valid token to refresh
       */
      if (this.isTokenValid() === false) {
        return
      }

      /*
       * do nothing, user is inactive for more than specified timeout
       */
      if (this.isInactivityTimeoutExceeded() === true) {
        return
      }

      /*
       * do nothing, token has more time to live than refresh trigger
       */
      if (this.shouldRefreshToken() === false) {
        return
      }

      handler()
    }, this.refreshInterval.intervalTimeout)
  }

  startTokenGuardInterval(
    logoutCallback: () => void,
    inactivityCallback: (flag: boolean, time: number) => void
  ): void {
    this.tokenGuardInterval.interval = setInterval(() => {
      if (this.isInactivityTimeoutExceeded() || this.isTokenValid() === false) {
        logoutCallback()
      }

      if (this.shouldShowInactivityModal()) {
        inactivityCallback(true, this.getTimeToLogout())
      } else {
        inactivityCallback(false, 0)
      }
    }, this.tokenGuardInterval.intervalTimeout)
  }

  listeners(): void {
    this.event.forEach((event) => {
      window.addEventListener(event, () => {
        this.setLastActivityTimestamp(dayjs().unix())
      })
    })
  }

  removeAll(): void {
    if (this.refreshInterval.interval) {
      clearInterval(this.refreshInterval.interval)
      this.refreshInterval.interval = null
    }

    if (this.tokenGuardInterval.interval) {
      clearInterval(this.tokenGuardInterval.interval)
      this.tokenGuardInterval.interval = null
    }

    this.tokenExpirationStorage.value = '0'
    this.lastActivityStorage.value = '0'
  }

  // Expiration timestamp
  getTokenExpirationTimestamp(): number {
    const timestamp = parseInt(this.tokenExpirationStorage.value, 10)
    return Number.isNaN(timestamp) ? 0 : timestamp
  }

  setTokenExpirationTimestamp(timestamp: number): TokenService {
    this.tokenExpirationStorage.value = timestamp.toString()
    return this
  }

  // Last activity
  getLastActivityTimestamp(): number {
    const timestamp = parseInt(this.lastActivityStorage.value, 10)
    return Number.isNaN(timestamp) ? 0 : timestamp
  }

  setLastActivityTimestamp(timestamp: number): TokenService {
    this.lastActivityStorage.value = timestamp.toString()
    return this
  }

  // Getters
  getTokenTtl(): number {
    const currentTimestamp = dayjs().unix()
    const ttl = this.getTokenExpirationTimestamp() - currentTimestamp
    return ttl < 0 ? 0 : ttl
  }

  getInactivityTime(): number {
    return dayjs().unix() - this.getLastActivityTimestamp()
  }

  getTimeToLogout(): number {
    return token.INACTIVITY_TIME_LIMIT - this.getInactivityTime()
  }

  isTokenValid(): boolean {
    return this.getTokenTtl() > 0
  }

  getTokenMaxTtl(): number {
    return token.TOKEN_MAX_TTL
  }

  shouldRefreshToken(): boolean {
    return this.getTokenTtl() < this.getTokenMaxTtl() - token.TOKEN_TTL_REFRESH_TRIGGER
  }

  shouldShowInactivityModal(): boolean {
    return this.isTokenValid() && this.getInactivityTime() >= token.INACTIVITY_TIME_MODAL_TRIGGER
  }

  isInactivityTimeoutExceeded(): boolean {
    return this.getInactivityTime() >= token.INACTIVITY_TIME_LIMIT
  }
}

// Export singleton instance to maintain compatibility with existing usage
const tokenService = new TokenServiceImpl()
export default tokenService

// Also export the class for testing purposes
export { TokenServiceImpl }
